/**
 * Free Gift Functionality
 * Handles adding free gifts to cart and auto-removal when threshold not met
 */

class FreeGiftManager {
  constructor() {
    this.init();
  }

  init() {
    // Listen for cart updates to check threshold
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.handleCartUpdate(event);
    });

    // Use event delegation on the free gift section instead of individual button listeners
    this.setupEventDelegation();

    // Initialize add gift buttons
    this.initAddGiftButtons();

    // Re-initialize when cart drawer content updates
    const observer = new MutationObserver(() => {
      // Re-setup event delegation when DOM changes
      this.setupEventDelegation();
      this.initAddGiftButtons();
    });

    const cartDrawer = document.querySelector('cart-drawer-items');
    if (cartDrawer) {
      observer.observe(cartDrawer, { childList: true, subtree: true });
    }
  }

  setupEventDelegation() {
    // Remove existing delegation listener if any (attach to cart drawer which doesn't get replaced)
    if (this.delegationHandler) {
      const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#CartDrawer');
      if (cartDrawer) {
        cartDrawer.removeEventListener('click', this.delegationHandler, true);
      }
    }

    // Create new delegation handler
    this.delegationHandler = (e) => {
      // Only handle clicks on free gift buttons
      const button = e.target.closest('.free-gift-add-btn');
      if (!button) return;

      // Check if button is disabled
      if (button.disabled || button.hasAttribute('disabled')) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      this.addFreeGift(button);
    };

    // Attach to cart drawer (which doesn't get replaced) for event delegation
    const cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#CartDrawer');
    if (cartDrawer) {
      cartDrawer.addEventListener('click', this.delegationHandler, true); // Use capture phase
    }
  }

  initAddGiftButtons() {
    // This method is kept for potential future use but currently just ensures buttons are ready
    // Event delegation handles all clicks, so no individual button listeners needed
  }

  async addFreeGift(button) {
    const variantId = button.dataset.variantId;
    
    if (!variantId) {
      return;
    }

    // Disable button during add
    button.disabled = true;
    button.textContent = 'Adding...';

    // Check if fetchConfig exists
    if (typeof fetchConfig === 'undefined') {
      button.disabled = false;
      button.textContent = 'Add gift';
      return;
    }

    // Check if routes exists
    if (typeof routes === 'undefined' || !routes.cart_add_url) {
      button.disabled = false;
      button.textContent = 'Add gift';
      return;
    }

    // Use same config as product-form.js
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('properties[_free_gift]', 'true');
    
    const cartDrawer = document.querySelector('cart-drawer');
    
    if (cartDrawer) {
      const sectionsToRender = cartDrawer.getSectionsToRender();
      const sectionIds = sectionsToRender.map((section) => section.id).join(',');
      formData.append('sections', sectionIds);
      formData.append('sections_url', window.location.pathname);
      cartDrawer.setActiveElement(button);
    } else {
      // Fallback if cart drawer not found
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
    }

    config.body = formData;

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const cartData = await response.json();

      if (cartData.status) {
        // Error adding to cart
        button.disabled = false;
        button.textContent = 'Add gift';
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'free-gift',
          productVariantId: variantId,
          errors: cartData.errors || cartData.description,
          message: cartData.message,
        });
        return;
      }

      // Success - render cart drawer
      if (cartDrawer) {
        cartDrawer.renderContents(cartData);
      }

      // Publish cart update event
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'free-gift',
        cartData: cartData,
      });
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Add gift';
    }
  }

  async handleCartUpdate(event) {
    // Wait a bit for cart to fully update
    setTimeout(() => {
      this.checkAndRemoveFreeGift();
    }, 500);
  }

  async checkAndRemoveFreeGift() {
    // Fetch current cart state
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      const cart = await response.json();

      if (!cart || !cart.items || cart.items.length === 0) {
        return;
      }

      // Find free gift item
      const freeGiftItem = cart.items.find((item) => {
        return item.properties && item.properties._free_gift === 'true';
      });

      if (!freeGiftItem) {
        return; // No free gift in cart
      }

      // Get threshold from free gift section data attributes (after cart update)
      // Wait a bit for DOM to update
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const freeGiftSection = document.querySelector('.free-gift-section');
      if (!freeGiftSection) {
        return; // Free gift section not present (threshold not set)
      }

      const meetsThreshold = freeGiftSection.dataset.meetsThreshold === 'true';
      
      // If threshold not met, remove the free gift
      if (!meetsThreshold) {
        await this.removeFreeGift(freeGiftItem.key);
      }
    } catch (error) {
      // Silently handle errors
    }
  }

  async removeFreeGift(itemKey) {
    try {
      // Get current cart to find line number
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cart = await cartResponse.json();

      if (!cart || !cart.items) {
        return;
      }

      // Find the line number for the free gift item
      const freeGiftItem = cart.items.find((item) => item.key === itemKey);
      if (!freeGiftItem) {
        return;
      }

      // Remove the item by setting quantity to 0
      const body = JSON.stringify({
        updates: {
          [freeGiftItem.key]: 0,
        },
        sections: ['cart-drawer', 'cart-icon-bubble'],
      });

      const response = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        ...{ body },
      });

      const state = await response.text();
      const parsedState = JSON.parse(state);

      // Update cart drawer
      const cartDrawer = document.querySelector('cart-drawer-items');
      if (cartDrawer) {
        cartDrawer.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id)?.querySelector(section.selector) ||
            document.getElementById(section.id);
          if (elementToReplace && parsedState.sections[section.section]) {
            elementToReplace.innerHTML = cartDrawer.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'free-gift-removal',
          cartData: parsedState,
        });
      }
    } catch (error) {
      // Silently handle errors
    }
  }
}

// Initialize when DOM is ready
let freeGiftManagerInstance;
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    freeGiftManagerInstance = new FreeGiftManager();
    window.freeGiftManager = freeGiftManagerInstance;
  });
} else {
  freeGiftManagerInstance = new FreeGiftManager();
  window.freeGiftManager = freeGiftManagerInstance;
}

